import { interval } from 'rxjs';
import { Component, HostListener } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ModalController, PopoverController, ToastController } from '@ionic/angular';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { CreateInstanceComponent } from 'src/app/components/create-instance/create-instance.component';
import { CreateServerComponent } from 'src/app/components/create-server/create-server.component';
import { ConfirmationModalComponent } from 'src/app/components/confirmation-modal/confirmation-modal.component';
import { BuyCreditsModalComponent } from 'src/app/components/buy-credits-modal/buy-credits-modal.component';
import { AutoRechargeModalComponent } from 'src/app/components/auto-recharge-modal/auto-recharge-modal.component';
import { CreditsInfoModalComponent } from 'src/app/components/credits-info-modal/credits-info-modal.component';
import { OpenOtherProfileModalComponent } from 'src/app/components/open-other-profile-modal/open-other-profile-modal.component';
import { PlansI, TotalumInstanceI } from 'src/app/interfaces/instance.interface';
import { TotalumServerI } from 'src/app/interfaces/server.interface';
import { UserI } from 'src/app/interfaces/user.interface';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { PopoverHandlerService } from 'src/app/services/popover-handler.service';
import { UtilsService } from 'src/app/services/utils.service';
import { environment } from 'src/environments/environment';
import { set } from 'lodash';
import { PopoverMenuComponent } from 'src/app/components/popover-menu/popover-menu.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  templateUrl: './account.page.html',
  styleUrls: ['./account.page.scss']
})
export class AccountPage {

  userData: UserI= null as any;

  servers: TotalumServerI[] = [];

  freeInstances: TotalumInstanceI[] = [];
  starterInstances: TotalumInstanceI[] = [];
  sharedInstances: TotalumInstanceI[] = [];
  profesionalInstances: TotalumInstanceI[] = [];
  enterpriseInstances: TotalumInstanceI[] = [];
  apiInstances: TotalumInstanceI[] = [];

  serverCreating!: any;

  loading = false;
  activePopover: number = -1;
  copiedIndex: number = -1;

  // Search, filter, sort, pagination
  searchQuery: string = '';
  filterPlan: PlansI | 'all' = 'all';
  sortField: 'date' | 'name' | 'plan' = 'date';
  sortDirection: 'desc' | 'asc' = 'desc';
  currentPage: number = 1;
  pageSize: number = 80;


  readonly planLabels: Record<string, string> = {
    free: 'Free',
    starter: 'Starter',
    shared: 'Basic',
    profesional: 'Professional',
    enterprise: 'Enterprise',
    api: 'API',
    agent: 'Agent',
  };

  get availablePlans(): { value: PlansI; label: string; count: number }[] {
    const counts: Record<string, number> = {};
    for (const inst of this.allInstances) {
      counts[inst.plan] = (counts[inst.plan] || 0) + 1;
    }
    return Object.keys(counts)
      .map(plan => ({ value: plan as PlansI, label: this.planLabels[plan] || plan, count: counts[plan] }));
  }

  get allInstances(): TotalumInstanceI[] {
    let serverInstances: TotalumInstanceI[] = [];
    this.servers.forEach((server: TotalumServerI) => {
      const instances = (server as any)['totalumInstancesPupulated'] || [];
      serverInstances = serverInstances.concat(instances);
    });
    return ([...this.freeInstances,  ...this.starterInstances, ...this.sharedInstances, ...this.profesionalInstances, ...this.enterpriseInstances, ...this.apiInstances, ...serverInstances] as any).filter((i: TotalumInstanceI) => i.plan !== 'agent');
  }

  /**
   * Server-side project list.
   *
   * Search / filter / sort / paging all happen in MongoDB — an account can own hundreds of
   * thousands of projects, so the previous approach (load every project, then filter and
   * slice in memory) could not work at that size. `paginatedInstances` holds only the
   * current page, and `totalInstances` is the true match count from the server.
   */
  paginatedInstances: TotalumInstanceI[] = [];
  totalInstances: number = 0;
  instancesLoading: boolean = false;
  private searchDebounce: any = null;
  private listRequestId: number = 0;

  get totalPages(): number {
    return Math.ceil(this.totalInstances / this.pageSize) || 1;
  }

  /** Kept for the template's "N projects" counter, which used to read the array length. */
  get filteredInstances(): { length: number } {
    return { length: this.totalInstances };
  }

  async loadInstancesPage() {
    // Guard against out-of-order responses: only the newest request may write state.
    const requestId = ++this.listRequestId;
    this.instancesLoading = true;
    try {
      const result = await this.apiSdkService.listInstances({
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        plan: this.filterPlan !== 'all' ? this.filterPlan : undefined,
        excludePlan: 'agent', // agent projects have their own page
        search: this.searchQuery.trim() || undefined,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
      } as any);

      if (requestId !== this.listRequestId) return;

      if (result?.success) {
        this.paginatedInstances = result.success.data.instances || [];
        this.totalInstances = result.success.data.total || 0;
        // A deletion on the last page can leave currentPage past the end — step back.
        if (this.paginatedInstances.length === 0 && this.currentPage > 1 && this.totalInstances > 0) {
          this.currentPage = Math.min(this.currentPage - 1, this.totalPages);
          await this.loadInstancesPage();
          return;
        }
      }
    } finally {
      if (requestId === this.listRequestId) this.instancesLoading = false;
    }
  }

  onSearchChange() {
    this.currentPage = 1;
    // Debounced so typing doesn't fire a query per keystroke.
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.loadInstancesPage(), 350);
  }

  onFilterChange() {
    this.currentPage = 1;
    this.loadInstancesPage();
  }

  onSortChange() {
    this.currentPage = 1;
    this.loadInstancesPage();
  }

  sortBy(field: 'date' | 'name' | 'plan') {
    if (this.sortField === field) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (field === 'date') {
        // Date is the default — just toggle between asc/desc
        this.sortDirection = 'asc';
      } else {
        // Reset non-date fields to default (date desc)
        this.sortField = 'date';
        this.sortDirection = 'desc';
      }
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadInstancesPage();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadInstancesPage();
    }
  }

  constructor(
    public formBuilder: FormBuilder,
    private router: Router,
    private apiSdkService: ApiSdkService,
    private route: ActivatedRoute,
    private modalCtrl: ModalController,
    private utilsService:UtilsService,
    private popoverCtrl: PopoverController,
    private popoverHandler: PopoverHandlerService,
    private toastCtrl: ToastController,
    private translate: TranslateService,
    ) { }

  async ngOnInit() {
    this.loading = true;
    await this.getUserData();

    // Auto-open the buy-credits modal when navigated here with ?openCredits=1
    // (e.g. from the locked API key CTA on /vcaas).
    if (this.route.snapshot.queryParamMap.get('openCredits') === '1') {
      // Strip the param so a refresh doesn't reopen the modal.
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openCredits: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.openBuyCreditsModal();
    }
  }

  async getUserData() {
    const result = await this.apiSdkService.getFullUserData();
    this.userData = result?.success?.data;
    if (!this.userData?.name) {
      this.router.navigate(['/user-data']);
    }
    await this.getInstances();
    await this.loadInstancesPage();
    await this.getServers();

    this.loading = false;
  }

  async getServers() {
    const servers = this.userData.totalumServers as TotalumServerI[] || [];
    this.serverCreating = null as any;
    // we populate the totalumInstancesAssigned of each server
    for (let server of servers) {
      // Fetched per server rather than filtered out of the user payload, which is capped.
      let totalumInstancesPopulated: TotalumInstanceI[] = [];
      try {
        const res = await this.apiSdkService.listInstances({ serverId: server._id as any, limit: 100 });
        if (res?.success) totalumInstancesPopulated = res.success.data.instances || [];
      } catch (e) {
        totalumInstancesPopulated = [];
      }
      /*
      const totalumInstancesAssigned = server.totalumInstancesAssigned || [];
      const totalumInstancesPopulated = [];
      if (totalumInstancesAssigned) {
        for (let totalumInstanceId of totalumInstancesAssigned) {
          const totalumInstance = await this.apiSdkService.getInstanceByOrganizationId(totalumInstanceId.organizationId);
          if (totalumInstance.success) {
            totalumInstancesPopulated.push(totalumInstance.success.data);
          }
        }
      }
      */
      server["totalumInstancesPupulated"] = totalumInstancesPopulated;
      if (server.status === 'creating') {
        this.serverCreating = server;
      }
    }
    if (this.serverCreating) {
      this.setServerCreatingInterval(this.serverCreating.name);
    }

    // show only servers with status active
    this.servers = servers.filter(server => server.status === 'active');


  }

  /** Per-plan totals from the server, so the counters stay right even when a bucket is capped. */
  planCounts: Record<string, number> = {};

  /** How many projects each per-plan section shows before you page the main list instead. */
  private readonly PLAN_BUCKET_LIMIT = 100;

  /**
   * Loads the per-plan sections from the server, one bounded query per plan.
   *
   * Previously these were bucketed out of `userData.totalumInstances`, which held every
   * project the account owned. That payload is now capped, and on a large account it could
   * never have been complete anyway — so each bucket is fetched (and bounded) on its own.
   */
  async getInstances(){
    const plans: { plan: PlansI; target: keyof AccountPage }[] = [
      { plan: 'free' as PlansI, target: 'freeInstances' },
      { plan: 'starter' as PlansI, target: 'starterInstances' },
      { plan: 'shared' as PlansI, target: 'sharedInstances' },
      { plan: 'profesional' as PlansI, target: 'profesionalInstances' },
      { plan: 'enterprise' as PlansI, target: 'enterpriseInstances' },
      { plan: 'api' as PlansI, target: 'apiInstances' },
    ];

    // Server-less projects only — the ones on a dedicated server render under that server.
    const results = await Promise.all(plans.map(p =>
      this.apiSdkService.listInstances({
        plan: p.plan,
        onlyWithoutServer: true,
        limit: this.PLAN_BUCKET_LIMIT,
        sortField: 'date',
        sortDirection: 'desc',
      } as any).catch(() => null)
    ));

    results.forEach((res, index) => {
      const key = plans[index].target as string;
      (this as any)[key] = res?.success ? (res.success.data.instances || []) : [];
      if (res?.success?.data?.planCounts) {
        this.planCounts = res.success.data.planCounts;
      }
    });
  }

  async openCreateInstanceModal(options:{plan?:PlansI, serverName?:string}, instance?: TotalumInstanceI) {
    /*if (this.userData.totalumInstances && this.userData.totalumInstances.length > 50) {
      const result = await this.popoverHandler.showAlertPopover("No puedes tener más de 50 proyectos por cuenta, si necesitas más, contacta con nosotros");
      return;
    }*/

    // Commented out: Free project limit alert
    // if ((this.freeInstances.length > 0 && options.plan === "free") && !instance) {
    //   if (this.userData.email!=='speedparadigm@gmail.com'){
    //     const result = await this.popoverHandler.showAlertPopover("Solo se permite tener un proyecto gratuito por cuenta");
    //     return;
    //   }
    // }
    const modal = await this.modalCtrl.create({
      component: CreateInstanceComponent,
      cssClass: 'create-instance-modal',
      componentProps: {
        options: options,
        instance: instance,
        instances: this.userData.totalumInstances,
        user: this.userData
      }
    });
    modal.present();

    // we get it's data
    const {data} = await modal.onDidDismiss();
    await this.getUserData();
  }

  async openCreateServerModal(server?: TotalumServerI) {
    /*if (this.userData.totalumServers && this.userData.totalumServers.length > 20) {
      const result = await this.popoverHandler.showAlertPopover("No puedes tener más de 20 servidores por cuenta, si necesitas más, contacta con nosotros");
      return;
    }*/

    if (this.serverCreating) {
      const result = await this.popoverHandler.showAlertPopover("Ya hay un servidor con creación en curso, espera a que termine");
      return;
    }

    const modal = await this.modalCtrl.create({
      component: CreateServerComponent,
      componentProps: {
        server: server
      }
    });
    modal.present();

    // we get it's data
    const {data} = await modal.onDidDismiss();
    await this.getUserData();

    // create an interval every 10 seconds to check if the server is active
    if (data.created){
      this.serverCreating = {name: data.serverName, status: 'creating'} as TotalumServerI;
      this.setServerCreatingInterval(data.serverName);
    }
  }

  goToBilling() {
    this.router.navigate(['/billing']);
  }

  async setServerCreatingInterval(serverName: string) {
    let counter = 0;
    const interval = setInterval(async () => {
      const result = await this.apiSdkService.getServerByName(serverName);
      if (result.success) {
        const server = result.success.data;
        if (server.status === 'active') {
          clearInterval(interval);
          await this.getUserData();
        }
      }
      counter++;
      if (counter === 100) {
        clearInterval(interval);
        const result = await this.popoverHandler.showAlertPopover("Ha habido un error creando el servidor, por favor, contacta con nosotros");
      }
    }, 10000);
  }

  async onShowServerPopoverMenu(event: any, server:TotalumServerI) {
    const popover = await this.popoverCtrl.create({
      component: PopoverMenuComponent,
      componentProps: {
        type: 'server'
      },
      event: event
    });
    await popover.present();

    const result = (await popover.onDidDismiss()).data;

    if (result === 'delete') {
      this.loading = true;
      await this.apiSdkService.deleteServerById(server._id as string);
      this.loading = false;
      await this.getUserData();
    }

    if (result === 'edit') {
      this.openCreateServerModal(server);  
    }

    if (result === 'create-instance') {
      this.openCreateInstanceModal({serverName: server.name})
    }
  }

  async onInteractInstance(type: 'edit' | 'delete', instance: TotalumInstanceI,serverName?: string) {
    if (type === 'delete') {
      this.loading = true;
      await this.apiSdkService.deleteInstanceById(instance._id as string);
      this.loading = false;
      await this.getUserData();
    }

    if (type === 'edit') {
      await this.openCreateInstanceModal({plan: instance.plan, serverName: serverName}, instance);
    }
  }

  onUpgrade(instance: TotalumInstanceI) {
    // Navigate to plan selection page with the organizationId
    // Plan is not specified, allowing user to select from available plans
    this.router.navigate(['/plan-selected'], {
      queryParams: {
        organizationId: instance.organizationId
      }
    });
  }

  getNextPlan(currentPlan: string): string | null {
    // Determine the next logical plan upgrade
    switch(currentPlan) {
      case 'free':
        return 'starter';
      case 'starter':
        return 'shared';
      case 'shared':
        return 'professional';
      case 'profesional':
        return 'enterprise';
      default:
        return null; // Already at highest plan or server plan
    }
  }

  togglePopover(index: number) {
    this.activePopover = this.activePopover === index ? -1 : index;
  }

  @HostListener('document:click', ['$event'])
  closePopoverOnClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.popover-trigger-btn') && !target.closest('.absolute.right-0.top-10')) {
      this.activePopover = -1;
    }
  }

  async onDeleteInstance(instance: TotalumInstanceI) {
    // Close the popover first
    this.activePopover = -1;

    // Open confirmation modal
    const modal = await this.modalCtrl.create({
      component: ConfirmationModalComponent,
      cssClass: 'confirmation-modal',
      componentProps: {
        title: 'Delete Project',
        message: 'Are you sure you want to permanently delete this project? All data, users, and configurations will be lost forever.',
        itemName: instance.organizationId,
        confirmText: 'Delete Project',
        cancelText: 'Cancel',
        isDangerous: true,
        confirmationText: instance.organizationId
      }
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();

    if (data?.confirmed) {
      this.loading = true;
      const result = await this.apiSdkService.deleteInstanceById(instance._id as string);
      if (result.error) {
        this.loading = false;
        const errorMsg = result.error?.error?.errors || result.error?.error?.message || result.error?.message || 'An unexpected error occurred while deleting the project.';
        const errorModal = await this.modalCtrl.create({
          component: ConfirmationModalComponent,
          cssClass: 'confirmation-modal',
          componentProps: {
            title: 'Error',
            message: errorMsg,
            confirmText: 'OK',
            cancelText: '',
            isDangerous: true
          }
        });
        await errorModal.present();
      } else {
        await this.getUserData();
        this.loading = false;
      }
    }
  }

  getInstanceUrl(instance: TotalumInstanceI): string {
    const organizationId = instance.organizationId;
    const cryptedDataString = instance.defaultUser as string;
    const cryptedDataStringEncoded = encodeURIComponent(cryptedDataString);
    return `${environment.totalumFrontendUrl}auth/login?auth=${cryptedDataStringEncoded}&organizationId=${organizationId}`;
  }

  copyInstanceLink(instance: TotalumInstanceI): void {
    const url = this.getInstanceUrl(instance);
    navigator.clipboard.writeText(url);
    const idx = this.paginatedInstances.indexOf(instance);
    this.copiedIndex = idx;
    setTimeout(() => { this.copiedIndex = -1; }, 1500);
  }

  async openTotalumInstance(instance: TotalumInstanceI) {
    const urlToOpen = this.getInstanceUrl(instance);
    window.open(urlToOpen, '_blank');
  }

  onChangePlan(instance: TotalumInstanceI) {
    // Close the popover first
    this.activePopover = -1;

    // Navigate to plan selection page with the organizationId
    this.router.navigate(['/plan-selected'], {
      queryParams: {
        organizationId: instance.organizationId
      }
    });
  }

  async openBuyCreditsModal() {
    const modal = await this.modalCtrl.create({
      component: BuyCreditsModalComponent,
      cssClass: 'buy-credits-modal',
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();

    // Refresh user data to get updated credits balance
    if (data?.purchased) {
      await this.getUserData();
    }
  }

  async openAutoRechargeModal() {
    // Only allow configuring auto-recharge once the user has a saved payment method.
    if (!this.userData?.stripeClientId) {
      const toast = await this.toastCtrl.create({
        message: this.translate.instant('AUTO_RECHARGE_NEEDS_PAYMENT'),
        duration: 4000,
        color: 'warning',
        position: 'top',
      });
      await toast.present();
      return;
    }

    const modal = await this.modalCtrl.create({
      component: AutoRechargeModalComponent,
      cssClass: 'auto-recharge-modal',
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.saved) {
      await this.getUserData();
    }
  }

  async onOpenOtherProfile(instance: TotalumInstanceI) {
    const url = this.getInstanceUrl(instance);
    const modal = await this.modalCtrl.create({
      component: OpenOtherProfileModalComponent,
      cssClass: 'open-other-profile-modal',
      componentProps: { instanceUrl: url }
    });
    await modal.present();
  }

  async openCreditsInfoModal() {
    const modal = await this.modalCtrl.create({
      component: CreditsInfoModalComponent,
      cssClass: 'credits-info-modal',
    });

    await modal.present();
  }

  isApiAccessEnabled(instance: TotalumInstanceI): boolean {
    if (instance.plan === 'api') {
      return instance.apiAccessEnabled !== false;
    }
    return instance.apiAccessEnabled === true;
  }

  async onToggleApiAccess(instance: TotalumInstanceI) {
    this.activePopover = -1;
    const newValue = !this.isApiAccessEnabled(instance);
    const result = await this.apiSdkService.toggleApiAccess(instance.organizationId, newValue);
    if (!result.error) {
      await this.getUserData();
    }
  }

  getPlanClass(instance: TotalumInstanceI): string {
    if (instance.plan === 'free') return 'plan-free';
    if (instance.plan === 'api') return 'plan-api';
    return 'plan-paid';
  }

  get userCredits(): number {
    return this.userData?.credits || 0;
  }
}
